import { Icon, Tooltip, token } from "@may-i/mash-design-system";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { isCameraAnalyzed } from "~/entities/camera/utils";
import { useAnalysisConfigs } from "~/features/analysis-config/hooks/use-analysis-configs";
import { useSelectedVersion } from "~/features/branch-version/hooks/use-selected-version";
import { useCameras } from "~/features/camera/hooks/use-cameras";

export function VersionStats() {
  const { branchUid } = useParams();
  const { t } = useTranslation("shared");
  const { selectedVersion } = useSelectedVersion();
  const versionId = selectedVersion?.id;
  const { data: cameras } = useCameras(branchUid, versionId);
  const { data: analysisConfigs } = useAnalysisConfigs(branchUid, versionId);

  const { cameraCount, cameraBreakdown, lineCount, zoneCount } = useMemo(() => {
    const camCount = cameras?.length ?? 0;
    let analyzed = 0;
    let unanalyzed = 0;
    let added = 0;
    let adjusted = 0;
    if (cameras) {
      for (const c of cameras) {
        if (isCameraAnalyzed(c.analysisNumber)) analyzed += 1;
        else unanalyzed += 1;
        if (c.mappingType === "add") added += 1;
        else if (c.mappingType === "edit") adjusted += 1;
      }
    }
    let lines = 0;
    let zones = 0;
    if (analysisConfigs) {
      for (const cfg of analysisConfigs) {
        lines += cfg.countingLines.length;
        zones += cfg.intervalZones.length;
      }
    }
    return {
      cameraCount: camCount,
      cameraBreakdown: { analyzed, unanalyzed, added, adjusted },
      lineCount: lines,
      zoneCount: zones,
    };
  }, [cameras, analysisConfigs]);

  if (!branchUid) return null;

  return (
    <Wrapper>
      <Tooltip
        trigger={
          <StatItem>
            <StatIcon icon="ic_video_on" width={14} />
            <StatValue>{cameraCount}</StatValue>
          </StatItem>
        }
        side="bottom"
        sideOffset={6}
      >
        <TooltipBreakdown>
          <BreakdownLine>{t("versionStats.cameraBreakdown.analyzed", { count: cameraBreakdown.analyzed })}</BreakdownLine>
          <BreakdownLine>{t("versionStats.cameraBreakdown.unanalyzed", { count: cameraBreakdown.unanalyzed })}</BreakdownLine>
          <BreakdownLine>{t("versionStats.cameraBreakdown.added", { count: cameraBreakdown.added })}</BreakdownLine>
          <BreakdownLine>{t("versionStats.cameraBreakdown.adjusted", { count: cameraBreakdown.adjusted })}</BreakdownLine>
        </TooltipBreakdown>
      </Tooltip>
      <StatItem>
        <StatIcon icon="ic_area_line" width={14} />
        <StatValue>{lineCount}</StatValue>
      </StatItem>
      <StatItem>
        <StatIcon icon="ic_area_zone" width={14} />
        <StatValue>{zoneCount}</StatValue>
      </StatItem>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StatIcon = styled(Icon)`
  color: ${token("colors.gray.GR800")};
`;

const StatValue = styled.span`
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  color: ${token("colors.gray.GR800")};
`;

const TooltipBreakdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const BreakdownLine = styled.span`
  font-size: 12px;
  line-height: 1.5;
  white-space: nowrap;
`;
